/**
 * Singleton resize manager - consolidates multiple resize listeners into a single one.
 * Uses requestAnimationFrame to batch updates and reduce main thread blocking (improves INP).
 *
 * @see Performance intervention: "Single resize manager — replace N identical listeners with singleton + rAF"
 */

import { breakpoints } from '../genericUtils/genericUtils';

type WindowSize = { width: number; height: number };
type Subscriber = (size: WindowSize) => void;

let rafId: number | null = null;
const subscribers = new Set<Subscriber>();

const getWindowSize = (): WindowSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const notifySubscribers = () => {
  rafId = null;
  const size = getWindowSize();
  subscribers.forEach((cb) => cb(size));
};

const handleResize = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(notifySubscribers);
};

let isListening = false;

const ensureListener = () => {
  if (!isListening) {
    isListening = true;
    window.addEventListener('resize', handleResize);
  }
};

const removeListener = () => {
  if (subscribers.size === 0 && isListening) {
    isListening = false;
    window.removeEventListener('resize', handleResize);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }
};

/**
 * Subscribe to window resize events. Returns an unsubscribe function.
 * All subscribers share a single resize listener + rAF for optimal performance.
 */
export function subscribeToResize(callback: Subscriber): () => void {
  subscribers.add(callback);
  ensureListener();
  return () => {
    subscribers.delete(callback);
    removeListener();
  };
}

/**
 * Get current window size (for initial state).
 */
export function getCurrentWindowSize(): WindowSize {
  return getWindowSize();
}

export function isMobile(): boolean {
  return window.innerWidth <= breakpoints.lg;
}
